import { useEffect, useMemo, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useOtherOpenSteps — hook that surfaces every OTHER currently-open
// step row for the given cook session, excluding the row the caller
// is actively viewing. Drives the CookMode multi-timer strip so the
// lead can glance at parallel timers (Marissa's chop, Trevor's other
// step's boil-water) without navigating away from their current
// step.
//
// Shape per entry:
//   {
//     id, user_id, step_id, step_title, started_at, nominal_seconds,
//     timer_state: { endsAt, isPaused, pausedRemainingSec, ... },
//     profile: { id, name, username }   — hydrated from profiles
//   }
//
// Filter rule: include if (user_id != viewerUserId) OR
// (user_id == viewerUserId && step_id != currentStepIdStr). The
// second clause is the "I'm viewing step 3 but I started a timer on
// step 1 too" case — surfaces my own parallel work back to me.
//
// Realtime: subscribes to cook_session_steps changes filtered by
// cook_session_id. INSERT/UPDATE on open rows refresh the map; rows
// flipped to finished_at != null get evicted.

export function useOtherOpenSteps({
  cookSessionId,
  viewerUserId,
  currentStepId,   // step the viewer is on right now, in CookMode
}) {
  const [byId, setById] = useState(() => new Map());
  const [profilesById, setProfilesById] = useState(() => new Map());

  // Initial fetch + realtime tail.
  useEffect(() => {
    if (!cookSessionId) return undefined;
    let alive = true;

    const fetchOnce = async () => {
      const { data: rows } = await supabase
        .from("cook_session_steps")
        .select("id, user_id, step_id, step_title, started_at, nominal_seconds, timer_state")
        .eq("cook_session_id", cookSessionId)
        .is("finished_at", null);
      if (!alive) return;
      const next = new Map();
      for (const r of (rows || [])) next.set(r.id, r);
      setById(next);
      // Hydrate profiles for all distinct user_ids in this set.
      const ids = Array.from(new Set((rows || []).map((r) => r.user_id).filter(Boolean)));
      if (ids.length > 0) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, name, username")
          .in("id", ids);
        if (!alive) return;
        const pm = new Map(profilesById);
        for (const p of (profs || [])) pm.set(p.id, p);
        setProfilesById(pm);
      }
    };
    fetchOnce();

    const ch = safeChannel(`rt:other-open-steps:${cookSessionId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps",
          filter: `cook_session_id=eq.${cookSessionId}` },
        (payload) => {
          if (!alive) return;
          const row = payload.new || payload.old;
          if (!row?.id) return;
          if (payload.eventType === "DELETE" || row.finished_at) {
            // Row closed → drop from the open set.
            setById((prev) => {
              if (!prev.has(row.id)) return prev;
              const next = new Map(prev);
              next.delete(row.id);
              return next;
            });
            return;
          }
          // INSERT or UPDATE of an open row → upsert into the map.
          setById((prev) => {
            const next = new Map(prev);
            next.set(row.id, row);
            return next;
          });
          // Lazily fetch the helper's profile if we don't have it yet.
          if (row.user_id && !profilesById.has(row.user_id)) {
            supabase.from("profiles")
              .select("id, name, username")
              .eq("id", row.user_id)
              .maybeSingle()
              .then(({ data }) => {
                if (!alive || !data) return;
                setProfilesById((prev) => {
                  const next = new Map(prev);
                  next.set(data.id, data);
                  return next;
                });
              });
          }
        },
      )
      .subscribe();

    return () => {
      alive = false;
      try { supabase.removeChannel(ch); } catch { /* ignore */ }
    };
    // profilesById intentionally NOT in deps — used for the
    // late-hydrate guard only; including it would refetch on every
    // profile add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookSessionId]);

  // Filter + decorate with profile.
  const rows = useMemo(() => {
    const currentStepStr = currentStepId != null ? String(currentStepId) : null;
    const currentStepBase = currentStepStr
      ? currentStepStr.replace(/-s\d+$/, "")
      : null;
    const out = [];
    for (const r of byId.values()) {
      // Exclude the viewer's own row for the step they're currently
      // looking at — that's the primary Timer onscreen, no need to
      // duplicate it as a pill.
      if (r.user_id === viewerUserId) {
        const rBase = String(r.step_id || "").replace(/-s\d+$/, "");
        if (currentStepBase && rBase === currentStepBase) continue;
      }
      out.push({
        ...r,
        profile: r.user_id ? profilesById.get(r.user_id) || null : null,
      });
    }
    // Sort by started_at ascending so the oldest-running timers (most
    // urgent) render first.
    out.sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
    return out;
  }, [byId, profilesById, viewerUserId, currentStepId]);

  return rows;
}
